import React, { useEffect, useMemo, useState } from "react";
import { ChevronRight, Lock } from "lucide-react";
import * as api from "../../services/api";
import { useSocial } from "../../contexts/SocialContext";
import useWorkoutSettings from "../../hooks/useWorkoutSettings";
import localStorageService from "../../services/localStorageService";
import { onOpenBadges, onShowEarned, openBadges, recentRareEffort, takeBadgesRequest } from "../../utils/badgeBus";
import RareEffort from "../profile/RareEffort";
import { cn } from "../../lib/utils";
import { Group } from "../profile/ProfileParts";
import DetailSheet from "../session/generate/DetailSheet";
import BadgeMedal from "./BadgeMedal";
import {
  badgeCaption,
  describeBadgeStatus,
  earnedFamilies,
  familyOf,
  groupByCategory,
  newlyEarned,
  nextTiers,
  nextUp,
  shortTier,
  splitBadges,
} from "./badgeDisplay";

// Badges (#153, #170). The definitions and the rules are on the server
// (api/lib/badges.js); this only draws what GET /api/badges returns.

// Earned badges you've already been shown, so a new one pops once.
const SEEN_KEY = "khronos_badges_seen";
// Earned badges you've looked at in the full list. Until then they're tagged
// New (#201): the pop is easy to miss, the tag waits for you.
const VIEWED_KEY = "khronos_badges_viewed";

/** Whether badges are switched on (Settings > Badges). */
export const useBadgesShown = () => {
  const [ws] = useWorkoutSettings();
  return ws.showBadges ?? true;
};

/** A progress bar that fills from empty when it first appears. */
function ProgressBar({ value, className }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setWidth(Math.min(100, value * 100)));
    return () => cancelAnimationFrame(id);
  }, [value]);
  return (
    <div className={cn("h-1.5 overflow-hidden rounded-full bg-muted", className)}>
      <div
        className="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

/** What the selected badge takes, and where you are with it. */
function BadgeDetail({ badge }) {
  const p = badge.progress;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/20 p-3" aria-live="polite">
      <BadgeMedal badge={badge} size="lg" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{badge.title}</p>
        <p className="text-xs text-muted-foreground">{badge.description}</p>
        <p className="mt-1 text-xs text-foreground/80">{describeBadgeStatus(badge)}</p>
        {!badge.earned_at && p && <ProgressBar value={p.current / p.target} className="mt-1.5" />}
      </div>
    </div>
  );
}

/** A grid of badges; tap one to see what it takes. */
export function BadgeGrid({ badges, popping, fresh, onOpen }) {
  const [selected, setSelected] = useState(null);
  const current = onOpen ? null : badges.find((b) => b.key === selected);
  return (
    <div className="grid gap-3">
      {current && <BadgeDetail badge={current} />}
      <div className="grid grid-cols-4 gap-x-2 gap-y-3">
        {badges.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={() => (onOpen ? onOpen(b.family) : setSelected(b.key === selected ? null : b.key))}
            aria-pressed={onOpen ? undefined : b.key === selected}
            aria-label={`${fresh?.has(b.key) ? "New: " : ""}${b.title}, ${describeBadgeStatus(b)}`}
            className={cn(
              "flex min-w-0 flex-col items-center gap-1 rounded-lg px-0.5 py-1.5 text-center transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
              b.key === selected && "bg-muted/40",
            )}
          >
            <BadgeMedal badge={b} pop={popping?.has(b.key)} isNew={fresh?.has(b.key)} />
            <span
              className={cn(
                "line-clamp-2 text-[11px] font-medium leading-tight",
                b.earned_at ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {b.title}
            </span>
            <span className="text-[10px] leading-none text-muted-foreground tabular-nums">
              {badgeCaption(b)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h3 className="px-0.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
    {children}
  </h3>
);

/** The few badges closest to being earned, each with its bar. */
function NextUp({ badges, onOpen }) {
  if (!badges.length) return null;
  return (
    <div className="grid gap-1">
      {badges.map((b) => (
        <button
          key={b.key}
          type="button"
          onClick={() => onOpen(b.family)}
          className="flex w-full items-center gap-2.5 rounded-lg px-1 py-1 text-left transition-colors hover:bg-muted/30"
        >
          <BadgeMedal badge={b} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs">
              <span className="font-medium text-foreground">{b.title}</span>
              <span className="text-muted-foreground">{` · ${describeBadgeStatus(b)}`}</span>
            </p>
            <ProgressBar value={b.progress.current / b.progress.target} className="mt-1" />
          </div>
        </button>
      ))}
    </div>
  );
}

const shortDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

/**
 * Earned badges as one medal per family (#203): your top tier, with a count
 * when you hold more than one. Grouped by category; tap one for its ladder.
 */
function FamilyGrid({ families, categories, onOpen, popping, fresh }) {
  const order = Array.isArray(categories) && categories.length ? categories : [{ key: "", title: "Earned" }];
  const groups = order
    .map((c) => ({ ...c, families: families.filter((f) => (f.category || "") === c.key || order.length === 1) }))
    .filter((g) => g.families.length > 0);
  return (
    <div className="grid gap-4">
      {groups.map((g) => (
        <section key={g.key} className="grid gap-2">
          <SectionTitle>{g.title}</SectionTitle>
          <div className="grid grid-cols-4 gap-x-2 gap-y-3">
            {g.families.map((f) => {
              const isNew = f.earned.some((b) => fresh?.has(b.key));
              const pop = f.earned.some((b) => popping?.has(b.key));
              const tiered = f.tiers.length > 1;
              const Cell = onOpen ? "button" : "div";
              return (
                <Cell
                  key={f.family}
                  {...(onOpen
                    ? {
                        type: "button",
                        onClick: () => onOpen(f.family),
                        "aria-label": `${isNew ? "New: " : ""}${f.top.title}, ${
                          tiered ? `${f.earned.length} of ${f.tiers.length} tiers` : describeBadgeStatus(f.top)
                        }`,
                      }
                    : {})}
                  className={cn(
                    "flex min-w-0 flex-col items-center gap-1 rounded-lg px-0.5 py-1.5 text-center",
                    onOpen &&
                      "transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
                  )}
                >
                  <span className="relative">
                    <BadgeMedal badge={f.top} pop={pop} isNew={isNew} />
                    {f.earned.length > 1 && (
                      <span className="absolute -bottom-0.5 -right-2 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-card bg-foreground px-1 text-[10.5px] font-bold tabular-nums text-background">
                        {f.earned.length}
                      </span>
                    )}
                  </span>
                  <span className="line-clamp-2 text-[11px] font-medium leading-tight text-foreground">{f.top.title}</span>
                  <span className="text-[10px] leading-none text-muted-foreground tabular-nums">
                    {tiered ? `${f.earned.length} of ${f.tiers.length}` : badgeCaption(f.top)}
                  </span>
                </Cell>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/** One family's ladder: every tier earned with its date, then the next. */
function FamilyLadder({ family }) {
  const { top, earned, next, tiers } = family;
  // A family you haven't started leads with its first tier.
  const head = top || next || tiers[0];
  const later = next ? tiers.slice(tiers.indexOf(next) + 1) : [];
  const unit = head.unit || "";
  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-3">
        <BadgeMedal badge={head} size="lg" />
        <div className="min-w-0">
          <p className="text-sm font-semibold">{head.title}</p>
          {head.description && <p className="text-xs text-muted-foreground">{head.description}</p>}
          <p className="mt-0.5 text-xs text-foreground/80">
            {tiers.length > 1 ? `${earned.length} of ${tiers.length} tiers` : describeBadgeStatus(head)}
          </p>
        </div>
      </div>
      <ol className="grid">
        {(tiers.length > 1 ? [...earned, ...(next ? [next] : [])] : []).map((b) => (
          <li key={b.key} className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2.5 py-1.5">
            <BadgeMedal badge={b} size="sm" />
            <span className="min-w-0">
              <span className={cn("block truncate text-sm", b.earned_at ? "text-foreground" : "text-muted-foreground")}>
                {b.title}
              </span>
              {!b.earned_at && b.progress && <ProgressBar value={b.progress.current / b.progress.target} className="mt-1" />}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {b.earned_at
                ? shortDate(b.earned_at)
                : b.progress
                  ? `${b.progress.current.toLocaleString("en-AU")} of ${b.progress.target.toLocaleString("en-AU")}`
                  : ""}
            </span>
          </li>
        ))}
      </ol>
      {later.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {`Then ${later.map((b) => shortTier(b.tier ?? 0)).join(", ")}${unit ? ` ${unit}` : ""}.`}
        </p>
      )}
    </div>
  );
}

/** All of your badges: next up, one medal per family earned, then the rest. */
function BadgesSheet({ open, onOpenChange, data, popping, fresh }) {
  const [familyKey, setFamilyKey] = useState(null);
  useEffect(() => {
    if (!open) setFamilyKey(null);
  }, [open]);
  const families = useMemo(() => earnedFamilies(data?.badges), [data]);
  const { locked } = splitBadges(data?.badges);
  const next = nextUp(data?.badges);
  const held = new Set(families.map((f) => f.family));
  // Families you haven't started: just their first tier. The rest of a family
  // you hold is in its ladder.
  const groups = groupByCategory(
    nextTiers(locked).filter((b) => !held.has(b.family)),
    data?.categories,
  );
  // Any badge opens its family here, earned or not, one at a time.
  const family = familyOf(data?.badges, familyKey);
  const earnedCount = families.reduce((n, f) => n + f.earned.length, 0);
  return (
    <DetailSheet
      open={open}
      onOpenChange={onOpenChange}
      title={family ? (family.top || family.next || family.tiers[0]).title : "Badges"}
      onBack={family ? () => setFamilyKey(null) : undefined}
    >
      {family ? (
        <FamilyLadder family={family} />
      ) : (
        <div className="grid gap-5">
          {next.length > 0 && (
            <section className="grid gap-2">
              <SectionTitle>Next up</SectionTitle>
              <NextUp badges={next} onOpen={setFamilyKey} />
            </section>
          )}
          {families.length > 0 && (
            <section className="grid gap-3">
              <p className="px-0.5 text-xs text-muted-foreground">{`Earned ${earnedCount} of ${data.total}, tap one for every tier`}</p>
              <FamilyGrid
                families={families}
                categories={data?.categories}
                onOpen={setFamilyKey}
                popping={popping}
                fresh={fresh}
              />
            </section>
          )}
          {groups.map((g) => (
            <section key={g.key} className="grid gap-2">
              <SectionTitle>{`${g.title} · still to earn`}</SectionTitle>
              <BadgeGrid badges={g.badges} onOpen={setFamilyKey} />
            </section>
          ))}
        </div>
      )}
    </DetailSheet>
  );
}

/** Your badges from the server, reloaded when one is earned; null while off. */
export function useMyBadges() {
  const { badgesVersion } = useSocial();
  const shown = useBadgesShown();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!shown) return undefined;
    let cancelled = false;
    api
      .getBadges("me")
      .then((d) => !cancelled && setData(d))
      .catch((err) => console.error("Load badges failed:", err));
    return () => {
      cancelled = true;
    };
  }, [badgesVersion, shown]);
  return shown ? data : null;
}

/** Your badges, on Activity (#204): the latest few, opening the full set. */
export function BadgesCard({ data }) {
  const shown = useBadgesShown();
  const [open, setOpen] = useState(false);

  const earned = useMemo(() => splitBadges(data?.badges).earned, [data]);
  // Which earned badges you haven't seen yet: they pop once, then are seen.
  const popping = useMemo(
    () => newlyEarned(earned, data ? localStorageService.get(SEEN_KEY) : null),
    [earned, data],
  );
  // Read before the effect above records them as seen: someone upgrading
  // starts from what has already popped, not with every badge New.
  const [viewed, setViewed] = useState(
    () => localStorageService.get(VIEWED_KEY) ?? localStorageService.get(SEEN_KEY) ?? null,
  );
  const fresh = useMemo(() => newlyEarned(earned, data ? viewed : []), [earned, data, viewed]);
  // The tags stay up in the sheet while it's open, then they're gone.
  const [sheetFresh, setSheetFresh] = useState(null);
  const markViewed = () => {
    const keys = earned.map((b) => b.key);
    localStorageService.set(VIEWED_KEY, keys);
    setViewed(keys);
  };
  useEffect(() => {
    if (data) localStorageService.set(SEEN_KEY, earned.map((b) => b.key));
    // First look ever: nothing is New.
    if (data && viewed == null) {
      const keys = earned.map((b) => b.key);
      localStorageService.set(VIEWED_KEY, keys);
      setViewed(keys);
    }
  }, [data, earned, viewed]);

  const openSheet = () => {
    setSheetFresh(fresh);
    markViewed();
    setOpen(true);
  };
  const openSheetRef = React.useRef(openSheet);
  openSheetRef.current = openSheet;
  // The badge toast's "See them": open once the badges have loaded.
  useEffect(() => {
    if (data?.badges && takeBadgesRequest()) openSheetRef.current();
    return onOpenBadges(() => {
      if (data?.badges && takeBadgesRequest()) openSheetRef.current();
    });
  }, [data]);

  if (!shown || !data?.badges) return null;
  // One medal per family, newest first, with how many tiers you hold.
  const latest = earnedFamilies(data.badges).slice(0, 5);
  const next = nextUp(data.badges, 1)[0];

  return (
    <Group
      title="Badges"
      note={fresh.size ? `${fresh.size} new · ${data.earned} of ${data.total}` : `${data.earned} of ${data.total}`}
    >
      <button
        type="button"
        onClick={openSheet}
        className="grid w-full gap-2.5 px-3 py-3 text-left transition-colors hover:bg-muted/30"
      >
        <span className="flex w-full items-center gap-2.5">
          {latest.length > 0 ? (
            <span className="flex min-w-0 flex-1 items-center gap-2.5 overflow-hidden py-1.5 pr-2">
              {latest.map((f) => (
                <span key={f.family} className="relative shrink-0">
                  <BadgeMedal
                    badge={f.top}
                    size="sm"
                    pop={f.earned.some((b) => popping.has(b.key))}
                    isNew={f.earned.some((b) => fresh.has(b.key))}
                  />
                  {f.earned.length > 1 && (
                    <span className="absolute -bottom-1 -right-1.5 grid h-4 min-w-4 place-items-center rounded-full border-2 border-card bg-foreground px-0.5 text-[9.5px] font-bold tabular-nums text-background">
                      {f.earned.length}
                    </span>
                  )}
                </span>
              ))}
            </span>
          ) : (
            <span className="flex min-w-0 flex-1 items-center gap-2.5 text-sm text-muted-foreground">
              <Lock className="h-4 w-4 shrink-0" />
              Finish a session to earn your first badge
            </span>
          )}
          <span className="shrink-0 text-xs text-muted-foreground">See all</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </span>
        {next && (
          <span className="block min-w-0">
            <span className="block truncate text-xs">
              <span className="text-muted-foreground">Next up: </span>
              <span className="font-medium text-foreground">{next.title}</span>
              <span className="text-muted-foreground">{` · ${describeBadgeStatus(next)}`}</span>
            </span>
            <ProgressBar value={next.progress.current / next.progress.target} className="mt-1" />
          </span>
        )}
      </button>
      <BadgesSheet open={open} onOpenChange={setOpen} data={data} popping={popping} fresh={sheetFresh} />
    </Group>
  );
}

/** A mate's earned badges, inside their sheet. */
export function MateBadges({ mateId }) {
  const shown = useBadgesShown();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!shown) return undefined;
    let cancelled = false;
    setData(null);
    setFailed(false);
    api
      .getBadges(mateId)
      .then((d) => !cancelled && setData(d))
      .catch((err) => {
        console.error("Load mate badges failed:", err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [mateId, shown]);

  if (!shown || failed) return null;
  if (!data?.badges) {
    return <p className="text-xs text-muted-foreground">{"Loading badges…"}</p>;
  }
  const families = earnedFamilies(data.badges);
  return (
    <section className="grid gap-2">
      <SectionTitle>{`Badges · ${data.earned} of ${data.total}`}</SectionTitle>
      {families.length > 0 ? (
        <FamilyGrid families={families} categories={data.categories} />
      ) : (
        <p className="text-sm text-muted-foreground">No badges yet.</p>
      )}
    </section>
  );
}

/**
 * Badges a session just earned (#204), in a sheet that rises after Save.
 * Opened by showEarned() (utils/badgeBus) with the badge keys from the
 * badges_earned notification; mounted once, at the app's root.
 */
export function BadgesEarnedSheet() {
  const [keys, setKeys] = useState(null);
  const [data, setData] = useState(null);
  useEffect(
    () =>
      onShowEarned((k) => {
        setKeys(k);
        api
          .getBadges("me")
          .then(setData)
          .catch(() => setData(null));
      }),
    [],
  );
  const families = useMemo(() => earnedFamilies(data?.badges), [data]);
  const earned = (data?.badges || []).filter((b) => keys?.includes(b.key));
  const next = nextUp(data?.badges, 1)[0];
  const close = () => {
    setKeys(null);
    setData(null);
  };
  const open = Boolean(keys?.length && earned.length);
  // The session's rare effort, if it had one, heads the sheet.
  const rare = open ? recentRareEffort() : null;
  return (
    <DetailSheet
      open={open}
      onOpenChange={(o) => !o && close()}
      title={earned.length === 1 ? "Badge earned" : `${earned.length} badges earned`}
      footer={
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={close}
            className="h-12 rounded-md border border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
          >
            Done
          </button>
          <button
            type="button"
            onClick={() => {
              close();
              openBadges();
            }}
            className="h-12 rounded-md border border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
          >
            All badges
          </button>
        </div>
      }
    >
      <div className="grid gap-4">
        {rare && <RareEffort effort={rare} className="justify-center text-sm" />}
        <div className="grid grid-cols-2 gap-2.5">
          {earned.map((b) => {
            const family = families.find((f) => f.family === b.family);
            const tiered = family && family.tiers.length > 1;
            return (
              <div
                key={b.key}
                className="grid justify-items-center gap-1.5 rounded-xl border border-border bg-background px-2 py-3 text-center"
              >
                <BadgeMedal badge={b} size="lg" pop />
                <span className="text-sm font-semibold">{b.title}</span>
                <span className="text-[11px] text-muted-foreground">
                  {tiered ? `New tier · ${family.earned.length} of ${family.tiers.length}` : b.description}
                </span>
              </div>
            );
          })}
        </div>
        {next && (
          <div>
            <p className="truncate text-xs">
              <span className="text-muted-foreground">Next up: </span>
              <span className="font-medium text-foreground">{next.title}</span>
              <span className="text-muted-foreground">{` · ${describeBadgeStatus(next)}`}</span>
            </p>
            <ProgressBar value={next.progress.current / next.progress.target} className="mt-1" />
          </div>
        )}
      </div>
    </DetailSheet>
  );
}

/** The badges a session earned (#204): awarded with that session's start. */
export const badgesForSession = (badges, startedAt) => {
  const at = Date.parse(startedAt || "");
  if (!Number.isFinite(at)) return [];
  return (badges || []).filter((b) => b.earned_at && Math.abs(Date.parse(b.earned_at) - at) < 1000);
};
